import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthTokenProvider } from "@/lib/auth-context";
import { createQueryClient } from "@/lib/query-client";
import { createTRPCClient, trpc } from "@/lib/trpc";
import {
	ClerkLoaded,
	ClerkLoading,
	ClerkProvider,
	SignIn,
	SignedIn,
	SignedOut,
	useAuth,
} from "@clerk/clerk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useState } from "react";
import { router } from "./router";

function getClerkPublishableKey() {
	const rawKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
	if (typeof rawKey !== "string") {
		return undefined;
	}

	const publishableKey = rawKey.trim();
	if (!publishableKey) {
		return undefined;
	}

	if (!publishableKey.startsWith("pk_test_") && !publishableKey.startsWith("pk_live_")) {
		console.warn("Ignoring invalid VITE_CLERK_PUBLISHABLE_KEY; running without Clerk auth.");
		return undefined;
	}

	return publishableKey;
}

const CLERK_PUBLISHABLE_KEY = getClerkPublishableKey();

/**
 * Inner app component that has access to Clerk's useAuth() hook.
 * Sets up tRPC client with the Clerk session token for authenticated API calls.
 */
function AppWithAuth() {
	const { getToken } = useAuth();
	const [queryClient] = useState(() => createQueryClient());
	const [trpcClient] = useState(() => createTRPCClient(getToken));

	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<AuthTokenProvider getToken={getToken}>
					<TooltipProvider delayDuration={200}>
						<ClerkLoading>
							<div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
								Loading...
							</div>
						</ClerkLoading>

						<ClerkLoaded>
							<SignedIn>
								<RouterProvider router={router} />
							</SignedIn>

							<SignedOut>
								<div className="flex min-h-screen items-center justify-center bg-background p-6">
									<SignIn />
								</div>
							</SignedOut>
						</ClerkLoaded>
					</TooltipProvider>
				</AuthTokenProvider>
			</QueryClientProvider>
		</trpc.Provider>
	);
}

/**
 * Fallback app for when Clerk is not configured (local development).
 * Uses a no-op getToken that always returns null, so tRPC falls back to
 * HMAC auth or unauthenticated requests.
 */
function AppWithoutAuth() {
	const [queryClient] = useState(() => createQueryClient());
	const [trpcClient] = useState(() => createTRPCClient(async () => null));

	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<AuthTokenProvider getToken={async () => null}>
					<TooltipProvider delayDuration={200}>
						<RouterProvider router={router} />
					</TooltipProvider>
				</AuthTokenProvider>
			</QueryClientProvider>
		</trpc.Provider>
	);
}

/**
 * Root App component.
 *
 * If VITE_CLERK_PUBLISHABLE_KEY is set, wraps the app with ClerkProvider
 * for authentication. Otherwise, runs without auth (for local development).
 */
export function App() {
	if (CLERK_PUBLISHABLE_KEY) {
		return (
			<ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
				<AppWithAuth />
			</ClerkProvider>
		);
	}

	return <AppWithoutAuth />;
}
